/**
 * Client-side Clerk session recovery for transient tRPC query UNAUTHORIZED (SC-2589).
 *
 * On eligible query failures: single-flight `clearCache` + `getToken({ skipCache: true })` (10s deadline,
 * 20s start-to-start cooldown), then one replay per query. Mutations are never replayed.
 *
 * Session API: registered from `_app` via `useClerkAuthRecoverySessionRegistration` →
 * `clerk-auth-recovery-session.registry.ts` (do not read `window.Clerk` here).
 * Telemetry: `clerk_auth_recovery` from `configureClerkAuthRecoveryCoordinator` in `_app`.
 * Passive diagnostics / batch RUM: `clerk-refresh-diagnostics.service.ts` (`trpc_unauthorized` from
 * `x-shelfcycle-trpc-unauthorized` set in `src/pages/api/trpc/[trpc].ts`).
 */
import { TRPCClientError } from "@trpc/client";
import { observable } from "@trpc/server/observable";

import { type AppRouter } from "~/server/api/root";
import { isTrpcUnauthorizedCode } from "~/shared/trpc/trpc-unauthorized-batch.constants";

import { getClerkAuthRecoverySession } from "./clerk-auth-recovery-session.registry";

export const CLERK_AUTH_RECOVERY_ACTION = "clerk_auth_recovery";

export const CLERK_AUTH_RECOVERY_DEADLINE_MS = 10_000;
export const CLERK_AUTH_RECOVERY_COOLDOWN_MS = 20_000;

export const CLERK_AUTH_RECOVERY_REPLAY_CONTEXT_KEY = "clerkAuthRecoveryReplayed";

export type ClerkAuthRecoveryAction = {
  diagnostic_version: 1;
  outcome: "success" | "timeout" | "rejected" | "empty_token";
};

export type ClerkAuthRecoveryEnvironment = {
  now: () => number;
  isBrowser: () => boolean;
  isOnline: () => boolean;
  getSession: () => ReturnType<typeof getClerkAuthRecoverySession>;
  raceWithDeadline: <T>(promise: Promise<T>, deadlineMs: number) => Promise<T | "timeout">;
};

type RecoveryAttemptResult = {
  success: boolean;
  outcome: ClerkAuthRecoveryAction["outcome"] | null;
};

export type ClerkAuthRecoveryCoordinator = {
  runRecoveryIfNeeded: () => Promise<boolean>;
  getNextRecoveryAllowedAt: () => number;
  resetForTests: () => void;
};

function defaultEnvironment(): ClerkAuthRecoveryEnvironment {
  return {
    now: () => Date.now(),
    isBrowser: () => typeof window !== "undefined",
    isOnline: () => typeof navigator === "undefined" || navigator.onLine,
    getSession: () => getClerkAuthRecoverySession(),
    raceWithDeadline: (promise, deadlineMs) => {
      let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<"timeout">(resolve => {
        timeoutHandle = setTimeout(() => resolve("timeout"), deadlineMs);
      });
      return Promise.race([promise, timeoutPromise]).finally(() => {
        if (timeoutHandle !== undefined) {
          clearTimeout(timeoutHandle);
        }
      });
    },
  };
}

export function isUnauthorizedTrpcClientError(error: unknown): error is TRPCClientError<AppRouter> {
  return error instanceof TRPCClientError && isTrpcUnauthorizedCode(error.data?.code);
}

export function hasClerkAuthRecoveryReplay(context: Record<string, unknown> | undefined): boolean {
  return context?.[CLERK_AUTH_RECOVERY_REPLAY_CONTEXT_KEY] === true;
}

function normalizeToken(token: string | null | undefined): string | null {
  if (token === null || token === undefined) {
    return null;
  }
  const trimmed = token.trim();
  return trimmed.length === 0 ? null : trimmed;
}

export function createClerkAuthRecoveryCoordinator(
  environment: ClerkAuthRecoveryEnvironment = defaultEnvironment(),
  emitRecoveryAction: (action: ClerkAuthRecoveryAction) => void = () => undefined,
): ClerkAuthRecoveryCoordinator {
  let recoveryInFlight: Promise<RecoveryAttemptResult> | null = null;
  let nextRecoveryAllowedAt = 0;

  const safeEmit = (outcome: ClerkAuthRecoveryAction["outcome"]) => {
    try {
      emitRecoveryAction({ diagnostic_version: 1, outcome });
    } catch {
      // Recovery telemetry must never affect query lifecycle.
    }
  };

  const applyRecoveryCooldown = () => {
    nextRecoveryAllowedAt = environment.now() + CLERK_AUTH_RECOVERY_COOLDOWN_MS;
  };

  const executeRecovery = async (): Promise<RecoveryAttemptResult> => {
    const session = environment.getSession();
    if (session === null || !session.isEligible()) {
      return { success: false, outcome: null };
    }

    try {
      session.clearCache();
    } catch {
      safeEmit("rejected");
      applyRecoveryCooldown();
      return { success: false, outcome: "rejected" };
    }

    applyRecoveryCooldown();

    let tokenResult: string | null | "timeout";
    try {
      tokenResult = await environment.raceWithDeadline(session.getToken(), CLERK_AUTH_RECOVERY_DEADLINE_MS);
    } catch {
      safeEmit("rejected");
      return { success: false, outcome: "rejected" };
    }

    if (tokenResult === "timeout") {
      safeEmit("timeout");
      return { success: false, outcome: "timeout" };
    }

    const token = normalizeToken(tokenResult);
    if (token === null) {
      safeEmit("empty_token");
      return { success: false, outcome: "empty_token" };
    }

    safeEmit("success");
    return { success: true, outcome: "success" };
  };

  const runRecoveryIfNeeded = (): Promise<boolean> => {
    if (recoveryInFlight !== null) {
      return recoveryInFlight.then(result => result.success);
    }

    const now = environment.now();
    if (now < nextRecoveryAllowedAt) {
      return Promise.resolve(false);
    }

    recoveryInFlight = executeRecovery().finally(() => {
      recoveryInFlight = null;
    });
    return recoveryInFlight.then(result => result.success);
  };

  return {
    runRecoveryIfNeeded,
    getNextRecoveryAllowedAt: () => nextRecoveryAllowedAt,
    resetForTests: () => {
      recoveryInFlight = null;
      nextRecoveryAllowedAt = 0;
    },
  };
}

let sharedEnvironment: ClerkAuthRecoveryEnvironment = defaultEnvironment();
let sharedCoordinator = createClerkAuthRecoveryCoordinator(sharedEnvironment);

export function configureClerkAuthRecoveryCoordinator(
  emitRecoveryAction: (action: ClerkAuthRecoveryAction) => void,
  environment?: ClerkAuthRecoveryEnvironment,
): void {
  sharedEnvironment = environment ?? defaultEnvironment();
  sharedCoordinator = createClerkAuthRecoveryCoordinator(sharedEnvironment, emitRecoveryAction);
}

export function resetClerkAuthRecoveryCoordinatorForTests(
  emitRecoveryAction: (action: ClerkAuthRecoveryAction) => void = () => undefined,
  environment?: ClerkAuthRecoveryEnvironment,
): ClerkAuthRecoveryCoordinator {
  sharedEnvironment = environment ?? defaultEnvironment();
  sharedCoordinator = createClerkAuthRecoveryCoordinator(sharedEnvironment, emitRecoveryAction);
  return sharedCoordinator;
}

function isQueryRecoveryEligible(op: { type: string; context: Record<string, unknown> }, error: unknown): boolean {
  const environment = sharedEnvironment;
  if (!environment.isBrowser()) {
    return false;
  }
  if (op.type !== "query") {
    return false;
  }
  if (!isUnauthorizedTrpcClientError(error)) {
    return false;
  }
  if (hasClerkAuthRecoveryReplay(op.context)) {
    return false;
  }
  if (!environment.isOnline()) {
    return false;
  }
  const session = environment.getSession();
  if (session === null || !session.isEligible()) {
    return false;
  }
  return true;
}

export function createClerkAuthRecoveryLink(): import("@trpc/client").TRPCLink<AppRouter> {
  return () =>
    ({ next, op }) =>
      observable(observer => {
        let innerUnsubscribe: (() => void) | undefined;
        let cancelled = false;

        const subscribeToOp = (operation: typeof op) => {
          innerUnsubscribe?.();
          const subscription = next(operation).subscribe({
            next(value) {
              if (cancelled) {
                return;
              }
              observer.next(value);
            },
            error(error: TRPCClientError<AppRouter>) {
              void handleError(error, operation);
            },
            complete() {
              if (cancelled) {
                return;
              }
              observer.complete();
            },
          });
          innerUnsubscribe = () => subscription.unsubscribe();
        };

        const handleError = async (error: TRPCClientError<AppRouter>, operation: typeof op) => {
          if (!isQueryRecoveryEligible(operation, error)) {
            if (!cancelled) {
              observer.error(error);
            }
            return;
          }

          const recoverySucceeded = await sharedCoordinator.runRecoveryIfNeeded();
          if (cancelled) {
            return;
          }
          if (!recoverySucceeded) {
            observer.error(error);
            return;
          }

          subscribeToOp({
            ...operation,
            context: {
              ...operation.context,
              [CLERK_AUTH_RECOVERY_REPLAY_CONTEXT_KEY]: true,
            },
          });
        };

        subscribeToOp(op);

        return () => {
          cancelled = true;
          innerUnsubscribe?.();
        };
      });
}
